import { create } from 'zustand';
import type { User, Room, Message } from '../types';

// ============================================================
// Auth Store
// ============================================================
interface AuthState {
  user: User | null;
  token: string | null;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: (() => {
    try { return JSON.parse(localStorage.getItem('chat_user') || 'null') as User | null; } catch { return null; }
  })(),
  token: localStorage.getItem('chat_token'),
  setAuth: (user, token) => {
    localStorage.setItem('chat_user', JSON.stringify(user));
    localStorage.setItem('chat_token', token);
    set({ user, token });
  },
  clearAuth: () => {
    localStorage.removeItem('chat_user');
    localStorage.removeItem('chat_token');
    set({ user: null, token: null });
  },
}));

// ============================================================
// Rooms Store
// ============================================================
interface RoomsState {
  rooms: Room[];
  activeRoomId: string | null;
  setRooms: (rooms: Room[]) => void;
  upsertRoom: (room: Room) => void;
  setActiveRoom: (id: string | null) => void;
  updateLastMessage: (roomId: string, message: Message) => void;
  incrementUnread: (roomId: string) => void;
  clearUnread: (roomId: string) => void;
  resetRooms: () => void;
}

export const useRoomsStore = create<RoomsState>((set) => ({
  rooms: [],
  activeRoomId: null,
  setRooms: (rooms) => set({ rooms }),
  upsertRoom: (room) =>
    set((state) => {
      const idx = state.rooms.findIndex((r) => r.id === room.id);
      if (idx >= 0) {
        const updated = [...state.rooms];
        updated[idx] = { ...updated[idx], ...room };
        return { rooms: updated };
      }
      return { rooms: [room, ...state.rooms] };
    }),
  setActiveRoom: (id) => set({ activeRoomId: id }),
  updateLastMessage: (roomId, message) =>
    set((state) => ({
      rooms: state.rooms
        .map((r) => r.id === roomId ? { ...r, last_message: message, updated_at: message.created_at } : r)
        .sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0)),
    })),
  incrementUnread: (roomId) =>
    set((state) => ({
      rooms: state.rooms.map((r) =>
        r.id === roomId ? { ...r, unread_count: (r.unread_count || 0) + 1 } : r
      ),
    })),
  clearUnread: (roomId) =>
    set((state) => ({
      rooms: state.rooms.map((r) => r.id === roomId ? { ...r, unread_count: 0 } : r),
    })),
  resetRooms: () => set({ rooms: [], activeRoomId: null }),
}));

// ============================================================
// Messages Store
// ============================================================
interface MessagesState {
  messagesByRoom: Record<string, Message[]>;
  hasMoreByRoom: Record<string, boolean>;
  nextCursorByRoom: Record<string, string | null>;
  setMessages: (roomId: string, messages: Message[], hasMore: boolean, nextCursor: string | null) => void;
  prependMessages: (roomId: string, messages: Message[], hasMore: boolean, nextCursor: string | null) => void;
  addMessage: (roomId: string, message: Message) => void;
  clearRoom: (roomId: string) => void;
  resetMessages: () => void;
}

export const useMessagesStore = create<MessagesState>((set) => ({
  messagesByRoom: {},
  hasMoreByRoom: {},
  nextCursorByRoom: {},
  setMessages: (roomId, messages, hasMore, nextCursor) =>
    set((state) => ({
      messagesByRoom: { ...state.messagesByRoom, [roomId]: messages },
      hasMoreByRoom: { ...state.hasMoreByRoom, [roomId]: hasMore },
      nextCursorByRoom: { ...state.nextCursorByRoom, [roomId]: nextCursor },
    })),
  prependMessages: (roomId, messages, hasMore, nextCursor) =>
    set((state) => ({
      messagesByRoom: {
        ...state.messagesByRoom,
        [roomId]: [...messages, ...(state.messagesByRoom[roomId] || [])],
      },
      hasMoreByRoom: { ...state.hasMoreByRoom, [roomId]: hasMore },
      nextCursorByRoom: { ...state.nextCursorByRoom, [roomId]: nextCursor },
    })),
  addMessage: (roomId, message) =>
    set((state) => {
      const current = state.messagesByRoom[roomId] || [];
      if (current.some((m) => m.id === message.id)) return {};
      return {
        messagesByRoom: {
          ...state.messagesByRoom,
          [roomId]: [...current, message],
        },
      };
    }),
  clearRoom: (roomId) =>
    set((state) => {
      const { [roomId]: _, ...rest } = state.messagesByRoom;
      return { messagesByRoom: rest };
    }),
  resetMessages: () => set({ messagesByRoom: {}, hasMoreByRoom: {}, nextCursorByRoom: {} }),
}));

// ============================================================
// Presence Store
// ============================================================
interface PresenceState {
  onlineUsers: Set<string>;
  typingUsers: Record<string, { userId: string; userName: string }[]>;
  setOnline: (userId: string, online: boolean) => void;
  setTyping: (roomId: string, userId: string, userName: string, isTyping: boolean) => void;
  resetPresence: () => void;
}

export const usePresenceStore = create<PresenceState>((set) => ({
  onlineUsers: new Set(),
  typingUsers: {},
  setOnline: (userId, online) =>
    set((state) => {
      const next = new Set(state.onlineUsers);
      if (online) next.add(userId); else next.delete(userId);
      return { onlineUsers: next };
    }),
  setTyping: (roomId, userId, userName, isTyping) =>
    set((state) => {
      const current = state.typingUsers[roomId] || [];
      const filtered = current.filter((u) => u.userId !== userId);
      const next = isTyping ? [...filtered, { userId, userName }] : filtered;
      return { typingUsers: { ...state.typingUsers, [roomId]: next } };
    }),
  resetPresence: () => set({ onlineUsers: new Set(), typingUsers: {} }),
}));
